import React from 'react';
import { useRouter, getSemanticPath } from '../contexts/RouterContext';
import type { NavigationState } from '../types';

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: NavigationState;
  children: React.ReactNode;
  activeClassName?: string;
}

// This logic must match the one in RouterContext.tsx to ensure consistency
const useHashRouter = window.location.hostname !== 'cma.globalfinx.in';

const Link: React.FC<LinkProps> = ({ to, children, className = '', activeClassName = '', ...props }) => {
  const { navigateTo, currentNav } = useRouter();

  // Destructure onClick from props to prevent it from overriding the anchor's onClick
  const { onClick, ...restProps } = props;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Allow default browser behavior (e.g., open in new tab)
    if (e.metaKey || e.ctrlKey) {
      return;
    }
    
    // Prevent full page reload
    e.preventDefault();
    
    // Perform client-side navigation
    navigateTo(to);
    
    // If an external onClick handler was provided, call it
    if (onClick) {
      onClick(e);
    }
  };
  
  const isActive = React.useMemo(() => {
    if (currentNav.page !== to.page) {
      return false;
    }
    const currentParams = currentNav.params || {};
    const toParams = to.params || {};
    
    if (Object.keys(currentParams).length !== Object.keys(toParams).length) {
      return false;
    }
    
    return Object.keys(toParams).every(key => toParams[key] === currentParams[key]);

  }, [currentNav, to]);

  const combinedClassName = `${className} ${isActive ? activeClassName : ''}`.trim();

  const pagePath = getSemanticPath(to);
  let href = pagePath;

  const searchParams = new URLSearchParams();
  if (to.params) {
    for (const [key, value] of Object.entries(to.params)) {
        // Skip params already encoded in semantic path
        const isCodeInPath = to.page === 'part-detail' && (value === 'part1' || value === 'part2');
        const isSlugInPath = to.page === 'article-detail';
        if (!isCodeInPath && !isSlugInPath) {
            searchParams.append(key, value);
        }
    }
  }

  if (searchParams.toString()) {
    href += `?${searchParams.toString()}`;
  }
  if (to.hash) {
      href += `#${to.hash}`;
  }

  return (
    <a href={href} onClick={handleClick} className={combinedClassName} {...restProps}>
      {children}
    </a>
  );
};

export default Link;
